import { useState, useId } from "react";
import {
  BlockStack,
  Collapsible,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Info, Globe, Link2, Cpu } from "lucide-react";

type Meta = {
  clientRoute?: string;
  apiPath?: string;
  referer?: string;
  source?: string;
};

type Props = {
  metadata: Meta | null | undefined;
};

function trimRef(s: string, max = 150) {
  if (s.length <= max) {
    return s;
  }
  return `${s.slice(0, max)}…`;
}

export function AuditProvenanceBlock({ metadata }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const id = useId();
  
  if (!metadata || typeof metadata !== "object") {
    return null;
  }
  
  const { clientRoute, apiPath, referer, source } = metadata;
  const hasLine =
    !!(clientRoute && String(clientRoute)) ||
    !!(apiPath && String(apiPath)) ||
    !!(referer && String(referer)) ||
    !!(source && String(source));
    
  if (!hasLine) {
    return null;
  }
  
  return (
    <BlockStack gap="100">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition duration-150 select-none py-1.5 px-2 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200/50 self-start"
        id={id}
      >
        <Info size={12} className="text-slate-400" />
        <span>{t("audit_provenance", { defaultValue: "Request Context" })}</span>
        {open ? <ChevronUp size={12} className="ml-0.5" /> : <ChevronDown size={12} className="ml-0.5" />}
      </button>

      <Collapsible
        id={`${id}-collapsible`}
        open={open}
        expandOnPrint
      >
        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 mt-1 select-all animate-fadeIn">
          <div className="grid grid-cols-1 gap-2.5">
            {clientRoute && (
              <div className="flex items-start gap-2 text-xs">
                <Globe size={13} className="text-slate-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <span className="font-semibold text-slate-500 mr-1.5">
                    {t("audit_meta_client_route", { defaultValue: "UI Route" })}:
                  </span>
                  <span className="font-mono text-slate-600 break-all">{String(clientRoute)}</span>
                </div>
              </div>
            )}
            
            {apiPath && (
              <div className="flex items-start gap-2 text-xs">
                <Link2 size={13} className="text-slate-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <span className="font-semibold text-slate-500 mr-1.5">
                    {t("audit_meta_api", { defaultValue: "API Endpoints" })}:
                  </span>
                  <span className="font-mono text-slate-600 break-all">{String(apiPath)}</span>
                </div>
              </div>
            )}
            
            {referer && (
              <div className="flex items-start gap-2 text-xs">
                <Globe size={13} className="text-slate-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <span className="font-semibold text-slate-500 mr-1.5">
                    {t("http_referer", { defaultValue: "HTTP Referer" })}:
                  </span>
                  <span className="font-mono text-slate-600 break-all">{trimRef(String(referer))}</span>
                </div>
              </div>
            )}
            
            {source && (
              <div className="flex items-start gap-2 text-xs">
                <Cpu size={13} className="text-slate-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <span className="font-semibold text-slate-500 mr-1.5">
                    {t("audit_meta_source", { defaultValue: "Source" })}:
                  </span>
                  <span className="font-mono text-slate-600 break-all">{String(source)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Collapsible>
    </BlockStack>
  );
}
